import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Subscription } from "@sunwing/wvi-components";
import { infoServiceClient } from "../../services/client";
import { RCL as useTranslation } from "../../components/RCL";
import { LinkConnector } from "../LinkConnector";

const EmailAcquisitionConnector = ({ data, language = "en" }) => {
	const { content, type, id, service, customFields, hiddenFields } = data;

	// Needed for tracking form activity
	const FORM_TIME_START = Math.floor(new Date().getTime() / 1000);
	const defaultFormId = "sign-up";
	const emailRegex =
		/^(([^&<>()[\]\\.,;:\s@'“‘”’]+(\.[^&<>()[\]\\.,;:\s@'“‘”’]+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	const [gatewayData, setGatewayData] = useState();
	const [fieldId, setFieldId] = useState();
	const [updatedContent, setUpdatedContent] = useState();

	const dictionary = {
		"contact-us-link-text": useTranslation({ searchKey: "contact-us-link-text" }),
		"privacy-policy-link-text": useTranslation({ searchKey: "PrivacyPolicylinktext" }),
		"email": useTranslation({ searchKey: "email" }),
		"submit": useTranslation({ searchKey: "submit" }),
		"subscription-ea-form-optin-message": useTranslation({
			searchKey: "subscription-ea-form-optin-message",
		}),
		"preferred-departure-city": useTranslation({ searchKey: "preferred-departure-city" }),
		"subscription-ea-form-optin-link-a11y": useTranslation({
			searchKey: "subscription-ea-form-optin-link-a11y",
		}),
		"subscription-ea-form-error-unknown": useTranslation({
			searchKey: "subscription-ea-form-error-unknown",
		}),
		"subscription-ea-form-email-type-mismatch": useTranslation({
			searchKey: "subscription-ea-form-email-type-mismatch",
		}),
		"subscription-ea-form-email-value-missing": useTranslation({
			searchKey: "subscription-ea-form-email-value-missing",
		}),
		"subscription-ea-form-gateway-value-missing": useTranslation({
			searchKey: "subscription-ea-form-gateway-value-missing",
		}),
		"subscription-ea-confirm-title": useTranslation({ searchKey: "subscription-ea-confirm-title" }),
		"subscription-ea-confirm-message": useTranslation({
			searchKey: "subscription-ea-confirm-message",
		}),
	};

	const getGatewaysArray = useCallback(async () => {
		return infoServiceClient
			.fetch(`/gateway?lang=${language}&type=package&alias=wes`)
			.then(res => {
				if (!res.ok) {
					if (res.status !== 200) {
						return res.json().then(json => {
							throw new Error(`${json.toString()} `);
						});
					}
				}
				return res.json();
			})
			.then(res => {
				const gateways = res.map(gateway => ({
					id: gateway.Code,
					value: `${gateway.Name} (${gateway.Code})`,
					label: `${gateway.Name} (${gateway.Code})`,
				}));

				return [{ title: "Gateways", items: gateways }];
			})
			.catch(err => {
				console.error("getGatewaysArray function error: ", err);
			});
	}, [language]);

	const processImages = (imagePath, height) => {
		let imagePathUpdate = imagePath;

		if (content && imagePathUpdate && height) {
			const needsCropping = /\/v[\d]+\//.test(imagePathUpdate);

			if (needsCropping) {
				const getMatch = imagePathUpdate.match(/\/(v[\d]+)\//);

				imagePathUpdate = imagePathUpdate.replace(
					getMatch?.[1] ? getMatch[1] : "",
					`f_auto,q_auto,h_${height}`
				);
			}
		}

		imagePathUpdate = imagePathUpdate.replace(
			"assets.sunwingtravelgroup.com",
			"assets.vacationvaults.ca"
		);

		return imagePathUpdate;
	};

	const sendAnalytics = () => {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: "email-form-submitted",
		});
	};

	const postFormSubmission = (userEmail, userGateway) => {
		// Submit to FormAssembly proxy form
		if (service) {
			// Create the key / value pair for payload
			const customFieldsTemp = customFields;
			const customFieldKeys = Object.keys(customFieldsTemp);
			const customPayload = {};

			// Handle Segment Variables
			const segmentTracking = window?.analytics?.user?.();

			customFieldKeys.forEach(key => {
				let storedValue = "";

				switch (key) {
					case "email":
						storedValue = userEmail;
						break;

					case "preferred_gateways_name":
						storedValue = userGateway?.value
							? userGateway.value.split("(")[0].trimEnd()
							: storedValue;
						break;

					case "preferred_gateways_code":
						storedValue = userGateway?.value
							? userGateway.value.split("(")[1].split(")")[0]
							: storedValue;
						storedValue = storedValue.split(",")[0];

						break;

					case "campaign_code":
						storedValue = customFields?.campaign_code?.value || storedValue;

						break;
					case "user_id":
						storedValue = segmentTracking ? segmentTracking.id() : storedValue;
						storedValue = storedValue === null ? "" : storedValue;
						break;

					case "anonymous_id":
						storedValue = segmentTracking ? segmentTracking.anonymousId() : storedValue;
						storedValue = storedValue === null ? "" : storedValue;
						break;

					default:
						break;
				}

				customPayload[customFieldsTemp[key].id] = storedValue || customFieldsTemp[key].value;
			});

			const tempPayload = { ...hiddenFields, ...customPayload };
			const payload = new FormData();

			const fields = Object.keys(tempPayload);

			fields.forEach(key => {
				payload.append(key, tempPayload[key]);
			});

			payload.append("tfa_dbTimeStarted", FORM_TIME_START);

			const apiConfig = {
				method: "POST",
				headers: new Headers(),
				body: payload,
			};

			const apiRequest = new Request(service);

			return fetch(apiRequest, apiConfig)
				.then(res => {
					if (!res.ok) {
						if (res.status !== 200) {
							// eslint-disable-next-line no-throw-literal
							throw { statusCode: res.status, errorMessage: res.statusText };
						}
					}
					return res.status;
				})
				.then(res => {
					if (res === 200) {
						sendAnalytics();
					}

					return { statusCode: res, errorMessage: "" };
				})
				.catch(error => {
					console.error("form submission failed: ", { error });
					// eslint-disable-next-line no-throw-literal
					throw { statusCode: error?.statusCode, errorMessage: error?.errorMessage };
				});
		}
	};

	const onSubmitForm = async (userEmail, userGateway) =>
		await postFormSubmission(userEmail, userGateway);

	useEffect(() => {
		/*
		STEP 1: Setup field ids
		*/
		if (id?.length) {
			setFieldId(id);
		} else {
			setFieldId(defaultFormId);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		/*
		STEP 2: Retrieve gateway data from storage or api, then store for session
		*/
		if (fieldId && typeof gatewayData === "undefined") {
			if (sessionStorage.getItem(`${fieldId}-sign-up-gateways`)) {
				const storedGateways = JSON.parse(sessionStorage.getItem(`${fieldId}-sign-up-gateways`));

				setGatewayData(storedGateways);
			} else {
				(async () => {
					const gateways = await getGatewaysArray();

					if (gateways) {
						setGatewayData(gateways);
						// Store gateways for session
						sessionStorage.setItem(`${fieldId}-sign-up-gateways`, JSON.stringify(gateways));
					}
				})();
			}
		}
	}, [fieldId, gatewayData, getGatewaysArray]);

	useEffect(() => {
		/*
		STEP 3: Process images
		*/
		if (content?.[type]?.mobileBanner?.image) {
			content[type].mobileBanner.image = processImages(content[type].mobileBanner.image, 140);
		}

		if (content?.[type]?.desktopWidget?.imageLarge) {
			content[type].desktopWidget.imageLarge = processImages(
				content[type].desktopWidget.imageLarge,
				486
			);
		}

		if (content?.[type]?.desktopWidget?.imageSmall) {
			content[type].desktopWidget.imageSmall = processImages(
				content[type].desktopWidget.imageSmall,
				547
			);
		}

		setUpdatedContent({ content });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type, content]);

	if (gatewayData?.length && fieldId && type && updatedContent) {
		return (
			<Subscription
				id={id}
				type={type}
				config={updatedContent}
				gatewayData={gatewayData}
				labels={{
					optInA11yMessage: dictionary["subscription-ea-form-optin-link-a11y"],
					optInMessage: dictionary["subscription-ea-form-optin-message"],
					optInPrivacyLabel: dictionary["privacy-policy-link-text"],
					optInPrivacyLink: `/${language}/${
						language === "fr" ? `politique-confidentialite` : `privacy-policy`
					}`,
					optInContactLabel: dictionary["contact-us-link-text"],
					optInContactLink: `/${language}/${language === "fr" ? `nous-contacter` : `contact-us`}`,
					inputFieldLabel: dictionary["email"],
					dropdownFieldLabel: dictionary["preferred-departure-city"],
					dropdownFieldErrorMissing: dictionary["subscription-ea-form-gateway-value-missing"],
					errorUnknown: dictionary["subscription-ea-form-error-unknown"],
					confirmTitle: dictionary["subscription-ea-confirm-title"],
					confirmMessage: dictionary["subscription-ea-confirm-message"],
					submit: dictionary["submit"],
				}}
				onSubmitForm={onSubmitForm}
				onValidateEmail={value => {
					// Returns an error message
					if (value && !emailRegex.test(value)) {
						return dictionary["subscription-ea-form-email-type-mismatch"];
					} else if (!value) {
						return dictionary["subscription-ea-form-email-value-missing"];
					}

					return "";
				}}
				renderLink={(href, props) => {
					return (
						<LinkConnector
							to={href}
							showExternalIcon={false}
							noStyle
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...props}
							target="_self"
						/>
					);
				}}
			/>
		);
	}

	return null;
};

EmailAcquisitionConnector.propTypes = {
	data: PropTypes.object.isRequired,
	language: PropTypes.oneOf(["en", "fr"]),
};

export default EmailAcquisitionConnector;
export { EmailAcquisitionConnector };
